import { Button, Input } from '@heroui/react'
import { posthog } from 'posthog-js'
import type React from 'react'
import { useState } from 'react'
import { useSnapshot } from 'valtio'
import { logError } from '@/components/Error/Error'
import { api } from '../../lib/api'
import { state } from '../../lib/state'

export const EmailPrompt = () => {
  const { userSettings, context, userEmail } = useSnapshot(state)
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)

  // If userSettings is undefined, we're still loading, so render nothing
  if (typeof userSettings === 'undefined') {
    return null
  }

  // Determine if we should show the prompt
  const shouldShowPrompt =
    !!userSettings &&
    !userSettings.emailProvided &&
    !userSettings.emailPromptDismissed

  // Show compact version if they previously dismissed it, or if userSettings is not loaded yet
  const shouldShowCompact =
    !userSettings ||
    (!!userSettings &&
      !userSettings.emailProvided &&
      userSettings.emailPromptDismissed)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsSubmitting(true)

    try {
      const result = await api.submitEmail(email)
      if (result.success) {
        // Update local state
        if (state.userSettings) {
          state.userSettings.emailProvided = true
          state.userSettings.emailPromptDismissed = false
        }
        state.userEmail = email

        // Update Featurebase if available
        if (typeof window.Featurebase === 'function' && context?.accountId) {
          window.Featurebase('identify', {
            organization: 'forgedapps',
            userId: context.accountId,
            name: email,
            email
          })
        }

        if (context?.accountId) {
          posthog.identify(context.accountId, {
            email,
            name: email
          })
        }

        setEmail('')
        setIsExpanded(false)
        setIsEditing(false)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit email')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDismiss = async () => {
    setIsExpanded(false)
    setIsEditing(false)
    try {
      await api.updateUserSettings({ emailPromptDismissed: true })
      if (state.userSettings) {
        state.userSettings.emailPromptDismissed = true
      }
    } catch (err) {
      logError('EmailPrompt', 'Failed to dismiss email prompt:', err)
    }
  }

  const handleShowPrompt = () => {
    setIsExpanded(true)
  }

  const handleClear = async () => {
    setIsSubmitting(true)
    setError(null)
    try {
      // Use the new api.clearEmail() method
      const result = await api.clearEmail()
      if (result.success) {
        if (state.userSettings) {
          state.userSettings.emailProvided = false
        }
        state.userEmail = ''
        setIsEditing(false)
        setIsExpanded(false)
        setEmail('')
      } else {
        setError('Failed to clear email')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear email')
    } finally {
      setIsSubmitting(false)
    }
  }

  // If user has already provided email, show the email (from PostHog or local)
  if (userEmail && !isEditing) {
    return (
      <div
        className="p-3 rounded-[3px] border bg-transparent email-prompt-blue-border"
        data-testid="email-prompt-root"
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-[var(--ds-text)]">
            Logged in as <b>{userEmail}</b>
          </span>
          <div className="flex gap-2">
            <Button
              variant="light"
              className="text-[var(--ds-text)] hover:text-[var(--ds-text)] rounded-[3px] h-8 px-2 text-sm"
              size="sm"
              disableRipple
              onPress={handleClear}
              isLoading={isSubmitting}
            >
              Logout
            </Button>
          </div>
        </div>
        {error && (
          <p className="text-xs text-[var(--ds-text-danger)] mt-1">{error}</p>
        )}
      </div>
    )
  }

  // If user has provided but no email found, just hide
  if (userSettings?.emailProvided && !userEmail && !isEditing) {
    return null
  }

  // Compact version for previously dismissed prompt or while loading userSettings
  if (shouldShowCompact && !isExpanded && !isEditing) {
    return (
      <div
        className="p-3 rounded-[3px] border bg-transparent email-prompt-blue-border"
        data-testid="email-prompt-root"
      >
        <div className="flex items-center justify-between">
          <span className="text-sm text-[var(--ds-text)]">
            Stay to date on Tech Radar!
          </span>
          <Button
            variant="solid"
            className="bg-primary text-white hover:bg-hover rounded-[3px] h-8 px-2 text-sm"
            size="sm"
            disableRipple
            onPress={handleShowPrompt}
          >
            Add Email
          </Button>
        </div>
      </div>
    )
  }

  // Full prompt version (for add or edit)
  if (shouldShowPrompt || isExpanded || isEditing) {
    return (
      <div
        className="p-4 rounded-[3px] border bg-transparent email-prompt-blue-border"
        data-testid="email-prompt-root"
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="text-sm font-medium text-[var(--ds-text)] mb-1">
              Stay up to date on Tech Radar
            </h3>
            <p className="text-xs text-[var(--ds-text-subtle)]">
              We'll only send important updates about new features.
            </p>
          </div>
          <Button
            size="sm"
            variant="light"
            className="text-[var(--ds-text)] hover:text-[var(--ds-text)] ml-2"
            onPress={handleDismiss}
          >
            ✕
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="flex gap-2">
            <Input
              autoFocus={true}
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              className="flex-1"
              classNames={{
                input:
                  'text-sm group-data-[has-value=true]:text-[var(--ds-text)] outline-none caret-[var(--ds-text)]',
                inputWrapper:
                  'border bg-[var(--ds-surface)] data-[hover=true]:bg-[var(--ds-surface)] focus-within:bg-[var(--ds-surface)] rounded-[3px] h-8 flex-1'
              }}
            />
            <Button
              type="submit"
              variant="solid"
              className="bg-primary text-white hover:bg-hover font-bold rounded-[3px] h-10 px-3"
              size="sm"
              disableRipple
              isLoading={isSubmitting}
              isDisabled={!email.trim()}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </div>
          {error && (
            <p className="text-xs text-[var(--ds-text-danger)] mt-1">{error}</p>
          )}
        </form>
      </div>
    )
  }

  return null
}
